import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoonLoader } from "react-spinners";
import Utils from "../../../utility";
import {
  getProjectDetails as fetchProjectDetails,
  updateProject as updateProjectDetails,
  generateSmartTemplateVariants
} from "../../../services/userService";
import centralizedTemplateService from "../../../services/centralizedTemplateService";
import TemplatePreviewRenderer from "../../../components/TemplatePreviewRenderer";

const TEMPLATES_PER_BATCH = 6;

const AllTemplatesEnhanced = ({
  projectDetailsState,
  projectDetailsGeneratedData,
  brandDetails
}) => {
  const currentUrl = window.location.href;
  const brandAndProjectId = useMemo(() => currentUrl.split("/"), [currentUrl]);
  const [projectDetails, setProjectDetails] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [polotnoTemplates, setPolotnoTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [templateError, setTemplateError] = useState(null);
  const [templatePreviews, setTemplatePreviews] = useState({});
  const [previewErrors, setPreviewErrors] = useState({});
  const [visibleCount, setVisibleCount] = useState(TEMPLATES_PER_BATCH);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const loadingRef = React.useRef(false);
  const loadedWithSmartTemplatesRef = React.useRef(false);
  const loadedWithGeneratedDataRef = React.useRef(false);
  const navigate = useNavigate();

  const resolvedProjectId = useMemo(() => {
    if (projectDetailsGeneratedData?.promptRes?.id) {
      return projectDetailsGeneratedData.promptRes.id;
    }

    if (projectDetailsGeneratedData?.promptRes?._id) {
      return projectDetailsGeneratedData.promptRes._id;
    }

    if (projectDetails?.id || projectDetails?._id) {
      return projectDetails.id || projectDetails._id;
    }

    const rawProjectId = brandAndProjectId[7];
    if (!rawProjectId) {
      return null;
    }

    return rawProjectId.includes("true") ? brandAndProjectId[8] : rawProjectId;
  }, [brandAndProjectId, projectDetails, projectDetailsGeneratedData]);

  const handlePreviewGenerated = useCallback((templateId, preview) => {
    setTemplatePreviews((prev) => {
      if (prev[templateId] === preview) {
        return prev;
      }
      return {
        ...prev,
        [templateId]: preview
      };
    });

    setPreviewErrors((prev) => {
      if (!prev[templateId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[templateId];
      return next;
    });
  }, []);

  const handleSaveTemplate = useCallback(
    async ({ navigateAfterSave = false, showToast = true } = {}) => {
      if (!selectedTemplateId) {
        Utils.failureToastMessage("Please select a template first.");
        return false;
      }

      if (!resolvedProjectId) {
        Utils.failureToastMessage(
          "Missing project information. Refresh and try again."
        );
        return false;
      }

      const selectedTemplate = polotnoTemplates.find(
        (template) => template.id === selectedTemplateId
      );

      const requestPayload = {
        selectedTemplateId
      };

      if (selectedTemplate?.template) {
        requestPayload.createdAdJson = JSON.parse(
          JSON.stringify(selectedTemplate.template)
        );
      }

      try {
        setIsSavingTemplate(true);
        const [error] = await Utils.parseResponse(
          updateProjectDetails(resolvedProjectId, requestPayload)
        );

        if (error) {
          const message =
            error?.response?.data?.message || "Failed to save template.";
          Utils.failureToastMessage(message);
          return false;
        }

        if (showToast) {
          Utils.successToastMessage("Template saved to project.");
        }

        setProjectDetails((prev) => ({
          ...prev,
          selectedTemplateId,
          createdAdJson: requestPayload.createdAdJson || prev?.createdAdJson
        }));

        if (navigateAfterSave) {
          navigate(
            `/dashboard/brand/details/project/finalReview/${resolvedProjectId}/${selectedTemplateId}`
          );
        }

        return true;
      } catch (error) {
        console.error("Failed to save template:", error);
        Utils.failureToastMessage("Unable to save template. Please try again.");
        return false;
      } finally {
        setIsSavingTemplate(false);
      }
    },
    [navigate, polotnoTemplates, resolvedProjectId, selectedTemplateId]
  );

  const handleDownloadAndContinue = useCallback(async () => {
    if (!selectedTemplateId) {
      Utils.failureToastMessage("Please select a template first.");
      return;
    }

    const preview = templatePreviews[selectedTemplateId];
    if (!preview) {
      Utils.failureToastMessage(
        "Preview is still generating. Please wait a moment."
      );
      return;
    }

    // Download the PNG
    const link = document.createElement("a");
    link.href = preview;
    link.download = `${selectedTemplateId}.png`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Save template and navigate to Final Review
    await handleSaveTemplate({ navigateAfterSave: true, showToast: false });
  }, [selectedTemplateId, templatePreviews, handleSaveTemplate]);

  const projectData = useMemo(() => {
    const selectedImage =
      projectDetailsState?.selectedImage ||
      projectDetails?.createdImage ||
      projectDetails?.selectedImage;

    // Fix: Use projectDetailsGeneratedData which contains AI-generated headlines/descriptions
    const generatedData =
      projectDetails?.generatedData ||
      projectDetailsGeneratedData?.promptRes?.generatedData ||
      projectDetailsGeneratedData;

    return {
      selectedImage,
      generatedData,
      brandDetails,
      projectGoal:
        projectDetails?.projectGoal || projectDetailsState?.projectGoal,
      platformName:
        projectDetails?.platformName || projectDetailsState?.platformName
    };
  }, [
    brandDetails,
    projectDetails,
    projectDetailsState,
    projectDetailsGeneratedData
  ]);

  const getProjectDetails = async (projectId) => {
    try {
      const [error, response] = await Utils.parseResponse(
        fetchProjectDetails(projectId)
      );
      if (error || !response) {
        console.error("Error");
        return;
      }
      setProjectDetails(response?.responseData);
    } catch (err) {
      console.error("An error occurred while fetching project details:", err);
    }
  };

  // Fetch Polotno templates
  const loadPolotnoTemplates = async () => {
    // Prevent duplicate loads
    if (loadingRef.current) {
      console.log("⏸️ Already loading templates, skipping...");
      return;
    }

    try {
      loadingRef.current = true;
      setIsLoadingTemplates(true);
      setTemplateError(null);
      setTemplatePreviews({});
      setPreviewErrors({});

      const templates = await centralizedTemplateService.getPolotnoTemplates();

      // Get fresh projectData at time of execution
      const currentProjectData = {
        selectedImage:
          projectDetailsState?.selectedImage ||
          projectDetails?.createdImage ||
          projectDetails?.selectedImage,
        // Fix: Use projectDetailsGeneratedData which contains AI-generated headlines/descriptions
        generatedData:
          projectDetails?.generatedData ||
          projectDetailsGeneratedData?.promptRes?.generatedData ||
          projectDetailsGeneratedData,
        brandDetails,
        projectGoal:
          projectDetails?.projectGoal || projectDetailsState?.projectGoal,
        platformName:
          projectDetails?.platformName || projectDetailsState?.platformName
      };

      // Check if we have pre-generated smart templates
      // They can come from either the project creation flow (projectDetailsGeneratedData)
      // or from loading an existing project (projectDetails)
      const smartTemplates =
        projectDetailsGeneratedData?.smartTemplates ||
        projectDetails?.smartTemplates;
      const hasSmartTemplates =
        smartTemplates &&
        Array.isArray(smartTemplates) &&
        smartTemplates.length > 0;

      console.log("📊 Current project data for templates:", {
        selectedImage: currentProjectData.selectedImage,
        hasGeneratedData: !!currentProjectData.generatedData,
        hasBrandDetails: !!currentProjectData.brandDetails,
        hasSmartTemplates: hasSmartTemplates,
        smartTemplateCount: smartTemplates?.length || 0,
        smartTemplateIds: smartTemplates?.map((st) => st.templateId),
        fetchedTemplateIds: templates.map((t) => t.id)
      });

      let enhancedTemplates;

      if (hasSmartTemplates) {
        // Use pre-generated smart templates (already have constraint-aware text)
        console.log(
          "🎯 Using pre-generated smart templates with constraint-aware text"
        );

        // First, apply smart templates to get constraint-aware text
        const templatesWithSmartText = templates.map((template) => {
          // Find matching smart template by ID
          const smartTemplate = smartTemplates.find(
            (st) => st.templateId === template.id
          );
          if (smartTemplate) {
            console.log(`✅ Found smart template for ${template.id}`);

            // Backend returns nested structure: smartTemplate.template.template contains Polotno JSON
            const polotnoJson =
              smartTemplate.template?.template || smartTemplate.template;

            console.log(
              `  - Smart template has ${polotnoJson?.pages?.length || 0} pages`
            );
            console.log(
              `  - Sample text from first page:`,
              polotnoJson?.pages?.[0]?.children
                ?.filter((child) => child.type === "text")
                ?.map((child) => child.text)
                ?.slice(0, 2)
            );
            return {
              ...template,
              template: polotnoJson
            };
          }
          // Fallback to original if no match
          console.warn(
            `⚠️ No smart template found for ${template.id}, using default`
          );
          return template;
        });

        // Track which templates actually got smart template matches
        const smartTemplateIds = new Set(
          smartTemplates.map((st) => st.templateId)
        );

        // Then, inject dynamic content into templates
        // Smart-matched templates: only inject images (text already optimized)
        // Unmatched templates: use full injection (legacy/constraint-based) to inject text
        console.log("🖼️ Injecting content into templates...");
        enhancedTemplates = await Promise.all(
          templatesWithSmartText.map((template) =>
            centralizedTemplateService.injectDynamicContent(
              template,
              currentProjectData,
              smartTemplateIds.has(template.id) // only true for actual smart template matches
            )
          )
        );
      } else {
        // Fallback: Transform templates to include dynamic content (OLD WAY)
        console.log("⚠️ No smart templates available, using legacy injection");
        enhancedTemplates = await Promise.all(
          templates.map((template) =>
            centralizedTemplateService.injectDynamicContent(
              template,
              currentProjectData
            )
          )
        );
      }

      setPolotnoTemplates(enhancedTemplates);
      loadedWithSmartTemplatesRef.current = hasSmartTemplates;
      loadedWithGeneratedDataRef.current = !!(
        currentProjectData.generatedData?.headlines?.length ||
        currentProjectData.generatedData?.description?.length
      );
      setVisibleCount(
        enhancedTemplates.length > 0
          ? Math.min(TEMPLATES_PER_BATCH, enhancedTemplates.length)
          : TEMPLATES_PER_BATCH
      );
      console.log(`✅ Loaded ${enhancedTemplates.length} templates`);
    } catch (error) {
      console.error("Failed to load Polotno templates:", error);
      setTemplateError("Failed to load templates. Using default templates.");
      // Fall back to default templates on error
      setPolotnoTemplates([]);
      setVisibleCount(TEMPLATES_PER_BATCH);
    } finally {
      setIsLoadingTemplates(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    // Message listener to handle navigation from iframe
    const handleIframeMessage = (event) => {
      // Get template editor origin for comparison
      const templateEditorOrigin = process.env.REACT_APP_TEMPLATE_EDITOR_URL
        ? new URL(process.env.REACT_APP_TEMPLATE_EDITOR_URL).origin
        : null;
      // Verify origin for security
      if (
        event.origin !== window.location.origin &&
        event.origin !== "https://dev.adperfect.ai" &&
        event.origin !== templateEditorOrigin
      ) {
        console.log("🚫 Blocked message from untrusted origin:", event.origin);
        return;
      }

      if (event.data.type === "NAVIGATE_TO_PROJECT") {
        const { projectId } = event.data;

        console.log("🔙 Navigating back to templates page from iframe:", {
          type: event.data.type,
          projectId,
          currentUrl
        });

        // Extract brandId from current URL
        // URL format: /dashboard/brand/details/{brandId}/{projectId}
        const urlParts = currentUrl.split("/");
        const brandId = urlParts[6]; // brandId is at index 6

        console.log("📋 Navigation details:", {
          brandId,
          projectId,
          urlParts
        });

        if (brandId && projectId) {
          // Navigate back to templates page
          window.location.href = `/dashboard/brand/details/${brandId}/${projectId}`;
        } else {
          console.warn(
            "⚠️ Missing brandId or projectId, staying on current page"
          );
        }
      } else if (event.data.type === "NAVIGATE_TO_FINAL_REVIEW") {
        const { projectId, templateId, url } = event.data;

        console.log("✅ Navigating to Final Review from iframe:", {
          type: event.data.type,
          projectId,
          templateId,
          url
        });

        // Navigate to the Final Review page
        if (url) {
          // Use the provided URL (includes both projectId and templateId)
          window.location.href = url;
        } else if (projectId && templateId) {
          // Fallback: construct URL from projectId and templateId
          window.location.href = `/dashboard/brand/details/project/finalReview/${projectId}/${templateId}`;
        } else if (projectId) {
          // Legacy fallback: just projectId
          window.location.href = `/dashboard/brand/details/project/finalReview/${projectId}`;
        }
      }
    };

    // Add event listener
    window.addEventListener("message", handleIframeMessage);

    // Cleanup
    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [currentUrl]);

  useEffect(() => {
    if (brandAndProjectId[7]) {
      if (brandAndProjectId[7]?.includes("true")) {
        getProjectDetails(brandAndProjectId[8]);
      } else {
        getProjectDetails(brandAndProjectId[7]);
      }
    }
  }, [brandAndProjectId]);

  // Load Polotno templates when project details are available
  // Only reload when specific data values change, not object references
  useEffect(() => {
    // Only load templates if we have at least some project data
    const hasImage =
      projectDetailsState?.selectedImage ||
      projectDetails?.createdImage ||
      projectDetails?.selectedImage;
    const hasGeneratedData =
      projectDetails?.generatedData || projectDetailsState?.generatedData;

    // Check if smart templates are available (from creation flow or loaded project)
    const hasSmartTemplates =
      projectDetailsGeneratedData?.smartTemplates?.length > 0 ||
      projectDetails?.smartTemplates?.length > 0;

    // Don't reload if we already have templates loaded WITH generatedData
    // UNLESS smart templates just became available (they contain AI-generated text)
    if (
      polotnoTemplates.length > 0 &&
      hasGeneratedData &&
      !hasSmartTemplates &&
      loadedWithGeneratedDataRef.current
    ) {
      console.log("✅ Templates already loaded with data, skipping reload");
      return;
    }

    // If we already loaded WITH smart templates, don't reload again
    if (
      polotnoTemplates.length > 0 &&
      hasSmartTemplates &&
      loadedWithSmartTemplatesRef.current
    ) {
      console.log(
        "✅ Templates already loaded with smart templates, skipping reload"
      );
      return;
    }

    // Also skip if already loading
    if (loadingRef.current) {
      console.log("⏸️ Already loading templates, skipping duplicate request");
      return;
    }

    // Require brandDetails to exist before loading
    if (!brandDetails) {
      console.log("⏸️ Waiting for brand details...");
      return;
    }

    // Add debounce to prevent rapid reloads
    const timeoutId = setTimeout(() => {
      if (hasImage || hasGeneratedData) {
        console.log("🔄 Loading templates with project data...");
        loadPolotnoTemplates();
      } else {
        console.log("⏸️ Waiting for project data before loading templates...");
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Use JSON.stringify to compare actual values, not object references
    JSON.stringify({
      selectedImage:
        projectDetails?.selectedImage || projectDetailsState?.selectedImage,
      createdImage: projectDetails?.createdImage,
      hasGeneratedData: !!(
        projectDetails?.generatedData || projectDetailsState?.generatedData
      ),
      brandId: brandDetails?.id,
      // Re-trigger when smart templates become available (AI-generated text)
      smartTemplateCount:
        (projectDetailsGeneratedData?.smartTemplates?.length || 0) +
        (projectDetails?.smartTemplates?.length || 0)
    })
  ]);

  useEffect(() => {
    if (!polotnoTemplates || polotnoTemplates.length === 0) {
      setTemplatePreviews((prev) => (Object.keys(prev).length ? {} : prev));
      setPreviewErrors((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }

    const validIds = new Set(polotnoTemplates.map((template) => template.id));

    setTemplatePreviews((prev) => {
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (validIds.has(key)) {
          next[key] = value;
        } else {
          changed = true;
        }
      });

      if (!changed && Object.keys(next).length === Object.keys(prev).length) {
        return prev;
      }
      return next;
    });

    setPreviewErrors((prev) => {
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (validIds.has(key)) {
          next[key] = value;
        } else {
          changed = true;
        }
      });

      if (!changed && Object.keys(next).length === Object.keys(prev).length) {
        return prev;
      }
      return next;
    });
  }, [polotnoTemplates]);

  useEffect(() => {
    if (projectDetails?.selectedTemplateId) {
      setSelectedTemplateId(projectDetails.selectedTemplateId);
    }
  }, [projectDetails?.selectedTemplateId]);

  const headingText =
    projectDetailsGeneratedData?.promptRes?.generatedData?.headlines?.[0] ||
    projectDetails?.generatedData?.headlines?.[0] ||
    "Your Heading Here";

  const subHeadingText =
    projectDetailsGeneratedData?.promptRes?.generatedData?.description?.[0] ||
    projectDetails?.generatedData?.description?.[0] ||
    "Your Subheading Here";

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(
      templateId === selectedTemplateId ? null : templateId
    );
  };

  const redirectToEditor = async (templateId) => {
    const projectId = resolvedProjectId;

    if (!projectId) {
      Utils.failureToastMessage(
        "Missing project information. Please try again."
      );
      return;
    }

    try {
      // Show loading state on this specific template's edit button
      setEditingTemplateId(templateId);

      // Use the already-enhanced template from the preview (same text the user sees)
      const enhancedTemplate = polotnoTemplates.find(
        (t) => t.id === templateId
      );

      if (enhancedTemplate?.template) {
        const [updateError] = await Utils.parseResponse(
          updateProjectDetails(projectId, {
            createdAdJson: enhancedTemplate.template,
            selectedTemplateId: templateId
          })
        );

        if (updateError) {
          console.error("Failed to save template:", updateError);
          Utils.failureToastMessage("Failed to save template");
          return;
        }

        navigate(`/editor/${templateId}/${projectId}`);
        return;
      }

      // Fallback: check for pre-generated smart templates
      const smartTemplates =
        projectDetailsGeneratedData?.smartTemplates ||
        projectDetails?.smartTemplates;

      console.log("🔍 Looking for pre-generated template:", {
        templateId,
        availableSmartTemplates: smartTemplates?.map((st) => st.templateId),
        smartTemplatesCount: smartTemplates?.length || 0,
        fromGeneratedData: !!projectDetailsGeneratedData?.smartTemplates,
        fromProjectDetails: !!projectDetails?.smartTemplates
      });

      const preGeneratedTemplate = smartTemplates?.find(
        (st) => st.templateId === templateId
      );

      console.log("📋 Pre-generated template search result:", {
        found: !!preGeneratedTemplate,
        hasTemplate: !!preGeneratedTemplate?.template,
        hasNestedTemplate: !!preGeneratedTemplate?.template?.template
      });

      if (preGeneratedTemplate) {
        console.log("🎯 Using pre-generated smart template for", templateId);
        console.log("📦 Smart template data to save:", {
          hasTemplate: !!preGeneratedTemplate.template,
          templateKeys: preGeneratedTemplate.template
            ? Object.keys(preGeneratedTemplate.template)
            : [],
          pageCount: preGeneratedTemplate.template?.pages?.length || 0,
          firstPageChildren:
            preGeneratedTemplate.template?.pages?.[0]?.children?.length || 0,
          templatePreview: JSON.stringify(
            preGeneratedTemplate.template
          ).substring(0, 300)
        });

        // Extract the Polotno JSON from potentially nested structure
        const polotnoTemplate =
          preGeneratedTemplate.template?.template ||
          preGeneratedTemplate.template ||
          preGeneratedTemplate;

        console.log("📝 Polotno template to save:", {
          hasPages: !!polotnoTemplate?.pages,
          pageCount: polotnoTemplate?.pages?.length,
          firstTextElement: polotnoTemplate?.pages?.[0]?.children?.find(
            (c) => c.type === "text"
          )?.text
        });

        // Save the pre-generated template to project
        const [updateError, updateResponse] = await Utils.parseResponse(
          updateProjectDetails(projectId, {
            createdAdJson: polotnoTemplate,
            selectedTemplateId: templateId
          })
        );

        if (updateError) {
          console.error("❌ Failed to save template to project:", updateError);
          Utils.failureToastMessage("Failed to save template");
          return;
        }

        console.log("✅ Saved pre-generated template to project");
        console.log("📋 Update response:", {
          success: !!updateResponse,
          responseKeys: updateResponse ? Object.keys(updateResponse) : [],
          hasCreatedAdJson: updateResponse?.createdAdJson ? "yes" : "no",
          responsePreview: JSON.stringify(updateResponse).substring(0, 300)
        });

        // Add a small delay to ensure database update propagates
        console.log("⏳ Waiting 500ms for database update to propagate...");
        await new Promise((resolve) => setTimeout(resolve, 500));
        console.log("✅ Proceeding to editor");

        // Navigate to editor (will load from project.createdAdJson)
        navigate(`/editor/${templateId}/${projectId}`);
      } else {
        // Fallback: Generate on-demand if no pre-generated template available
        console.warn("⚠️ No pre-generated template, generating on-demand...");

        const smartTemplateData = {
          templateId: templateId,
          projectId: projectId,
          brandDetails: {
            name: brandDetails?.brandName || brandDetails?.name,
            tone:
              brandDetails?.discovery?.tone ||
              brandDetails?.tone ||
              "professional",
            theme: brandDetails?.theme
          },
          productInfo: {
            name:
              projectDetailsState?.projectName || projectDetails?.projectName,
            description:
              projectDetailsState?.projectDescription ||
              projectDetails?.projectDescription
          },
          campaignInfo: {
            type:
              projectDetailsState?.platformName ||
              projectDetails?.platformName ||
              "general",
            keywords:
              projectDetailsState?.keywords || projectDetails?.keywords || []
          }
        };

        const [error] = await Utils.parseResponse(
          generateSmartTemplateVariants(smartTemplateData)
        );

        if (error) {
          console.error("Smart template generation failed:", error);
          Utils.failureToastMessage(
            error?.response?.data?.message ||
              "Failed to generate text for template"
          );
          return;
        }

        console.log(
          "✅ Generated smart template on-demand (saved to project by backend)"
        );

        // Navigate to editor
        navigate(`/editor/${templateId}/${projectId}`);
      }
    } catch (error) {
      console.error("Error in redirectToEditor:", error);
      Utils.failureToastMessage("Failed to open editor");
    } finally {
      setEditingTemplateId(null);
    }
  };

  const renderPolotnoTemplate = (template) => {
    const preview = templatePreviews[template.id];
    const previewError = previewErrors[template.id];

    const getAspectRatio = () => {
      const { width, height } = template.template;
      if (height > width) return "9/16";
      if (width > height) return "16/9";
      return "1/1";
    };

    return (
      <div
        className="relative overflow-hidden rounded-xl bg-grey-10"
        style={{
          aspectRatio: getAspectRatio(),
          width: "100%"
        }}
      >
        {preview ? (
          <img
            src={preview}
            alt={`${template.name} preview`}
            className="size-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-grey-10">
            {previewError ? (
              <>
                <div className="text-ft12-16 font-medium text-grey-14">
                  Preview unavailable
                </div>
              </>
            ) : (
              <>
                <MoonLoader loading={true} color="#17C471" size={22} />
                <div className="text-ft12-16 text-grey-14">
                  Generating preview...
                </div>
              </>
            )}
          </div>
        )}

        {/* Template name */}
        <div className="absolute left-2 top-2 z-10 rounded bg-white px-2 py-1 text-ft12-16 font-medium shadow">
          {template.name}
        </div>

        {/* Edit button */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (!editingTemplateId) {
              redirectToEditor(template.id);
            }
          }}
          className={`absolute -right-3 -top-2 z-10 flex size-12 items-center justify-center rounded-xl bg-white shadow-lg transition-shadow hover:shadow-xl ${
            editingTemplateId === template.id
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer"
          }`}
        >
          {editingTemplateId === template.id ? (
            <MoonLoader loading={true} color="#17C471" size={16} />
          ) : (
            <img src="/images/edit-icon.svg" alt="Edit" />
          )}
        </div>
      </div>
    );
  };

  const renderDefaultTemplate = (template) => {
    const selectedImage =
      projectData?.selectedImage || "/images/white-background.jpg";

    return (
      <div
        style={{
          backgroundImage: `${template?.linearGradient},url(${Utils.fixUrl(
            selectedImage
          )})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat"
        }}
        className="relative h-305px rounded-xl"
      >
        <div
          onClick={() => {
            if (!editingTemplateId) {
              redirectToEditor(template._id);
            }
          }}
          className={`absolute -right-3.5 -top-2 flex size-12 items-center justify-center rounded-xl bg-white shadow-pencilBoxShadow ${
            editingTemplateId === template._id
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer"
          }`}
        >
          {editingTemplateId === template._id ? (
            <MoonLoader loading={true} color="#17C471" size={16} />
          ) : (
            <img src="/images/edit-icon.svg" alt="Edit" />
          )}
        </div>

        <div
          style={{ ...template?.logo }}
          className={`${template?.logo?.display ? template?.logo?.display : "absolute"}  flex  items-center justify-center  font-InterBold text-ft12-16 text-white`}
        >
          {brandDetails?.theme?.logo ? (
            <img
              className="size-15"
              src={brandDetails?.theme?.logo}
              alt="Logo"
            />
          ) : (
            template.logo?.text || "LOGO"
          )}
        </div>

        {template?.heading && (
          <div
            style={{ ...template?.heading }}
            className={`absolute font-InterSemibold text-ft21-29.4 ${template?.fontColor}`}
          >
            {template?.heading?.text || headingText}
          </div>
        )}

        {template?.button && (
          <button
            style={{ ...template?.button }}
            className={`absolute flex h-6 w-111px items-center justify-center ${template?.button?.backGroundColor ? template?.button?.backGroundColor : "bg-white"}  font-InterMedium text-ft11-15.4 ${template?.button?.color ? template?.button?.color : "text-black-0 "} shadow-buttonShadow`}
          >
            LEARN MORE
          </button>
        )}
      </div>
    );
  };

  // Template rendering logic
  const renderTemplate = (template) => {
    // If it's a Polotno template with our new structure
    if (template.template && template.category) {
      return renderPolotnoTemplate(template);
    }

    // Fallback to original CSS-based rendering for default templates
    return renderDefaultTemplate(template);
  };

  const TemplateBox = ({ template }) => {
    const templateId = template.id || template._id;
    const isSelected = selectedTemplateId === templateId;

    return (
      <div
        onClick={() => handleTemplateSelect(templateId)}
        className={`relative cursor-pointer rounded-xl ${
          isSelected ? "border-4 border-green-12" : "border"
        }`}
        style={{ borderStyle: isSelected ? "solid" : "none" }}
      >
        {renderTemplate(template)}
      </div>
    );
  };

  // Combine Polotno templates with default templates
  const allTemplates = useMemo(() => {
    const templates = [...polotnoTemplates];

    // Default templates (fallback)
    const defaultTemplate = [
      {
        _id: "66f5266f04862dccbe803c19",
        size: "SQUARE",
        fontColor: "text-black-0",
        linearGradient:
          "linear-gradient( rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1) )",
        heading: {
          top: "180px",
          left: "32px",
          width: "80%",
          text: headingText
        },
        subHeading: {
          top: "180px",
          left: "32px",
          width: "80%",
          text: subHeadingText
        },
        button: {
          bottom: "21px",
          left: "24px"
        },
        logo: {
          left: "24px",
          top: "24px",
          text: brandDetails?.name || "LOGO",
          imageUrl: brandDetails?.theme?.logo || ""
        }
      }
      // Add more default templates as needed
    ];

    // Add default templates if no Polotno templates or as fallback
    if (templates.length === 0 || templateError) {
      templates.push(...defaultTemplate);
    }

    return templates;
  }, [
    polotnoTemplates,
    templateError,
    headingText,
    subHeadingText,
    brandDetails
  ]);

  const totalTemplates = allTemplates.length;

  const visibleTemplates = useMemo(() => {
    if (totalTemplates === 0) {
      return [];
    }
    return allTemplates.slice(0, Math.min(visibleCount, totalTemplates));
  }, [allTemplates, totalTemplates, visibleCount]);

  const hasMoreTemplates = visibleCount < totalTemplates;

  // Return all visible templates for preview rendering
  // Include template content hash to detect when smart templates are loaded
  const templatesForPreview = useMemo(() => {
    return polotnoTemplates
      .slice(0, visibleCount)
      .filter((template) => template?.template && template.id);
  }, [
    JSON.stringify(
      polotnoTemplates.map((t) => ({
        id: t.id,
        // Include first text element to detect smart template changes
        text:
          t.template?.pages?.[0]?.children?.find((c) => c.type === "text")
            ?.text || ""
      }))
    ),
    visibleCount
  ]);

  const handleLoadMoreTemplates = useCallback(() => {
    setVisibleCount((prev) => {
      const next = prev + TEMPLATES_PER_BATCH;
      return totalTemplates > 0 ? Math.min(next, totalTemplates) : next;
    });
  }, [totalTemplates]);

  const selectedTemplatePreview = selectedTemplateId
    ? templatePreviews[selectedTemplateId]
    : null;

  const selectedTemplateHasError = selectedTemplateId
    ? !!previewErrors[selectedTemplateId]
    : false;

  return (
    <div className="h-auto w-full rounded-3xl bg-white px-11 pb-10 pt-19">
      <h1 className="font-InterBold text-ft24-32 text-grey-12">
        Generated Ads
      </h1>
      <p className="mt-1.5 font-InterRegular text-ft16-19.36 font-normal text-grey-14">
        Click a template to select it. Use the toolbar on each card to open the
        editor, and the actions below to save or download your chosen design.
      </p>

      {/* Loading state */}
      {isLoadingTemplates && (
        <div className="mt-5 flex justify-center">
          <MoonLoader loading={true} color="#17C471" size={40} />
          <span className="ml-3 text-grey-14">
            Loading Polotno templates...
          </span>
        </div>
      )}

      {/* Error state */}
      {templateError && (
        <div className="mt-5 rounded-lg bg-yellow-10 p-4">
          <p className="text-ft14-19.6 text-yellow-10">{templateError}</p>
        </div>
      )}

      {/* Templates grid */}
      {!isLoadingTemplates && visibleTemplates.length > 0 && (
        <div
          className="mt-5"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "20px",
            gridAutoRows: "auto"
          }}
        >
          {visibleTemplates.map((template, index) => {
            const { width, height } = template.template || {};
            let gridColumn = "span 2";
            let gridRow = "span 2";
            let sizeReason = "default";

            // Determine grid sizing based on template type
            if (
              template.type === "SQUARE" ||
              (width && height && width === height)
            ) {
              // Square templates: 2 columns x 2 rows
              gridColumn = "span 2";
              gridRow = "span 2";
              sizeReason =
                template.type === "SQUARE"
                  ? "type:SQUARE"
                  : "dimensions:square";
            } else if (
              template.type === "STORY" ||
              (height && width && height > width)
            ) {
              // Portrait templates: 1 column x 2 rows
              gridColumn = "span 1";
              gridRow = "span 2";
              sizeReason =
                template.type === "STORY"
                  ? "type:STORY"
                  : "dimensions:portrait";
            } else if (
              template.type === "BANNER" ||
              (width && height && width > height)
            ) {
              // Landscape templates: 2 columns x 1 row
              gridColumn = "span 2";
              gridRow = "span 1";
              sizeReason =
                template.type === "BANNER"
                  ? "type:BANNER"
                  : "dimensions:landscape";
            }

            // Log grid sizing for new templates (landscape/portrait promo templates)
            if (
              template.id &&
              (template.id.includes("landscape") ||
                template.id.includes("portrait"))
            ) {
              console.log(`📏 Grid sizing for ${template.id}:`, {
                type: template.type,
                width,
                height,
                gridColumn,
                gridRow,
                reason: sizeReason
              });
            }

            return (
              <div
                key={template.id || template._id || `template-${index}`}
                style={{
                  gridColumn,
                  gridRow
                }}
              >
                <TemplateBox template={template} />
              </div>
            );
          })}
        </div>
      )}

      {!isLoadingTemplates && visibleTemplates.length === 0 && (
        <div className="mt-8 rounded-xl bg-grey-10 p-6 text-center text-ft14-19.6 text-grey-14">
          No templates available right now. Try generating again or edit one of
          your existing designs.
        </div>
      )}

      {hasMoreTemplates && !isLoadingTemplates && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleLoadMoreTemplates}
            className="flex h-9 cursor-pointer items-center justify-center rounded-3xl border border-grey-14 px-5 font-InterBold text-ft12-16 text-grey-14 transition hover:border-green-12 hover:text-green-12"
          >
            Load More Templates
          </button>
        </div>
      )}

      {/* Action buttons */}
      {selectedTemplateId && !isLoadingTemplates && (
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <button
            onClick={handleDownloadAndContinue}
            disabled={
              !selectedTemplatePreview ||
              selectedTemplateHasError ||
              isSavingTemplate
            }
            className={`flex h-8.7 w-48 cursor-pointer items-center justify-center rounded-2xl px-4 text-ft12-16 text-white ${
              !selectedTemplatePreview ||
              selectedTemplateHasError ||
              isSavingTemplate
                ? "bg-green-12/60"
                : "bg-green-12"
            }`}
          >
            {isSavingTemplate ? "Saving…" : "Download & Continue"}
          </button>
          <button
            onClick={() => redirectToEditor(selectedTemplateId)}
            disabled={!!editingTemplateId}
            className={`flex h-8.7 w-32 items-center justify-center rounded-2xl border border-green-12 bg-white px-4 text-ft12-16 text-green-12 hover:bg-green-12/5 ${
              editingTemplateId
                ? "cursor-not-allowed opacity-50"
                : "cursor-pointer"
            }`}
          >
            {editingTemplateId ? (
              <MoonLoader loading={true} color="#17C471" size={16} />
            ) : (
              "Edit Template"
            )}
          </button>
        </div>
      )}

      {selectedTemplateHasError && !isLoadingTemplates && (
        <div className="mt-3 text-center text-ft12-16 text-grey-14">
          Preview failed to load for this template. Please open the editor to
          view the full design.
        </div>
      )}

      {templatesForPreview.length > 0 && (
        <TemplatePreviewRenderer
          templates={templatesForPreview}
          onPreviewGenerated={handlePreviewGenerated}
        />
      )}
    </div>
  );
};

export default AllTemplatesEnhanced;
